import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setSessionToken } from "@/lib/api";
import { initIAP } from "@/lib/iap";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip /me check; AuthCallback will handle it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Belt-and-braces: under no circumstances do we want `loading` stuck at true.
    // iPad WKWebView has been observed to stall credentialed cross-origin requests
    // (Apple 2.1(a) rejection root cause). Force-resolve after 4s no matter what.
    const failSafe = setTimeout(() => setLoading(false), 4000);
    refresh().finally(() => clearTimeout(failSafe));
    return () => clearTimeout(failSafe);
  }, [refresh]);

  // Configure RevenueCat once we know the user_id (Capacitor native only).
  // We set appUserID = our internal user_id so the /billing/webhook can match
  // RevenueCat events back to the right Hush user. Idempotent — initIAP
  // short-circuits if already configured.
  useEffect(() => {
    if (user?.user_id) {
      initIAP(user.user_id).catch(() => {});
    }
  }, [user?.user_id]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const register = async (email, password, name, referralCode) => {
    try {
      const body = { email, password, name };
      if (referralCode) body.referral_code = referralCode;
      const { data } = await api.post("/auth/register", body);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("[AuthContext] logout request failed:", err);
    }
    setSessionToken("");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
